/**
 * Dashboard types for the modern stack.
 *
 * Shape mirrors `src/services/dashboardService.ts` so the same backend payload
 * resolves into both legacy + modern dashboards while the migration overlaps.
 * When the legacy service is decommissioned this file becomes the single
 * source of truth.
 */

export interface DashboardKPIs {
  activeScaffolds: number;
  activeWorkOrders: number;
  quotesPending: number;
  totalQuotes: number;
  inventoryItems: number;
  lowStockItems: number;
}

export interface ScaffoldStatusBreakdown {
  planned: number;
  inProgress: number;
  draft: number;
}

export interface DashboardWorkOrder {
  id: string;
  workOrderNumber: string;
  clientName: string;
  /** API returns string (e.g. "Planned", "In Progress") or number. */
  status: string | number;
  /** ISO date-time string. */
  created: string;
}

export interface DashboardQuote {
  id: string;
  quoteNumber: string;
  clientName: string;
  /** API returns string (e.g. "Under Review") or number. */
  status: string | number;
  totalAmount: number;
  /** Alternative field name returned by some endpoints. */
  total?: number;
}

export interface DashboardMeeting {
  id: string;
  meetingTitle: string;
  clientName: string;
  meetingLocation?: string;
  /** Full date-time ISO string. */
  meetingDate: string;
  /** HH:mm:ss. */
  meetingStartTime: string;
  /** May arrive as number or string from the API. */
  meetingStatus?: number | string;
}

export interface InventoryHealth {
  totalItems: number;
  lowStockItems: number;
  /** "Excellent" | "Good" | "Attention Needed". */
  healthScore?: string;
}

export type SystemAlertType = 'success' | 'warning' | 'info' | 'error';

export interface SystemAlert {
  id: number;
  title: string;
  /** Relative time string (e.g. "2h ago"). */
  time: string;
  type: SystemAlertType;
}

export interface AdminDashboardResponse {
  /** API returns "kpIs" (capital I). */
  kpIs?: DashboardKPIs;
  /** Fallback for lowercase variant. */
  kpis?: DashboardKPIs;
  scaffoldStatusBreakdown: ScaffoldStatusBreakdown;
  recentWorkOrders: DashboardWorkOrder[];
  pendingQuotes: DashboardQuote[];
  upcomingMeetings: DashboardMeeting[];
  inventoryHealth: InventoryHealth;
  systemAlerts?: SystemAlert[];
  /** ISO date-time string. */
  lastUpdated?: string;
}

/**
 * Normalize work-order status to a numeric enum value. Replaces the
 * `normalizeWorkOrderStatus` helper in `services/dashboardService.ts`.
 */
export function normalizeWorkOrderStatus(status: string | number): number {
  if (typeof status === 'number') return status;
  const statusMap: Record<string, number> = {
    Draft: 1,
    Planned: 2,
    'In Progress': 3,
    InProgress: 3,
    Closed: 4,
  };
  return statusMap[status] ?? parseInt(status, 10) ?? 1;
}

export function normalizeQuoteStatus(status: string | number): number {
  if (typeof status === 'number') return status;
  const statusMap: Record<string, number> = {
    Draft: 1,
    'Under Review': 2,
    UnderReview: 2,
    Approved: 3,
    Sent: 4,
    'Change Requested': 5,
    ChangeRequested: 5,
    Awarded: 6,
    Rejected: 7,
    Expired: 8,
    WorkOrderGenerated: 9,
  };
  return statusMap[status] ?? parseInt(status, 10) ?? 1;
}

export function normalizeMeetingStatus(
  status: string | number | undefined,
): number | undefined {
  if (status === undefined || status === null) return undefined;
  if (typeof status === 'number') return status;
  const statusMap: Record<string, number> = {
    Scheduled: 1,
    Completed: 2,
    Cancelled: 3,
    Rescheduled: 4,
    '1': 1,
    '2': 2,
    '3': 3,
    '4': 4,
  };
  return statusMap[status] ?? parseInt(status, 10) ?? undefined;
}
